/**
 * Draft Manager
 *
 * Manages auto-saving and restoration of form drafts for the Democratic Services PWA.
 * Provides seamless draft saving/restoration for petitions and consultations.
 *
 * @package DemocraticServices
 * @since 3.1.0
 * @author Democratic Services Team
 *
 * Design Decisions:
 * - Uses IndexedDB via offline-queue.js for persistent storage
 * - Debounced auto-save to reduce storage operations
 * - Form identification via unique IDs (page + form type)
 * - Automatic cleanup after successful submission
 * - User notification when draft is restored
 */

import {
	saveDraft,
	getDraft,
	removeDraft,
	getAllDrafts,
} from './offline-queue.js';

// =============================================================================
// CONFIGURATION
// =============================================================================

const DRAFT_CONFIG = {
	// Auto-save debounce delay (milliseconds)
	saveDebounceMs: 2000,

	// Selector for forms that should have draft saving
	// Comprehensive list includes all petition and consultation form variations
	formSelectors: [
		// Explicit data attribute
		'form[data-draft-enabled]',
		// Petition forms
		'#petition-create-form',
		'#petition-sign-form',
		'#create-petition-form',
		'#sign-petition-form',
		'#petition-signature-form',
		'.petition-form',
		'.petition-create-form',
		'.petition-sign-form',
		'.signature-form',
		'form[data-action="sign_petition"]',
		'form[data-action="create_petition"]',
		// Consultation forms
		'#consultation-response-form',
		'#consultation-form',
		'#submit-consultation-response',
		'.consultation-form',
		'.consultation-response-form',
		'form[data-action="submit_consultation"]',
		// Newsletter forms
		'.newsletter-form-ajax',
		'#newsletter-form',
	],

	// Fields to exclude from draft saving (e.g., security tokens)
	excludedFields: [
		'_wpnonce',
		'_wp_http_referer',
		'action',
		'nonce',
		'petition_nonce',
		'consultation_nonce',
		'_submission_id',
		'password',
		'confirm_password',
		'card_number',
		'cvv',
	],

	// Storage keys for local storage backup
	localStoragePrefix: 'democratic_draft_',
};

/**
 * Draft type constants
 */
export const DRAFT_TYPES = {
	PETITION: 'petition',
	CONSULTATION: 'consultation',
	GENERIC: 'generic',
};

// =============================================================================
// STATE
// =============================================================================

/**
 * Map of form elements to their save timeouts
 * @type {Map<HTMLFormElement, number>}
 */
const saveTimeouts = new Map();

/**
 * Map of form elements to their last saved data (for comparison)
 * @type {Map<HTMLFormElement, string>}
 */
const lastSavedData = new Map();

/**
 * Registered forms
 * @type {Set<HTMLFormElement>}
 */
const registeredForms = new Set();

// =============================================================================
// DRAFT ID GENERATION
// =============================================================================

/**
 * Generate a unique draft ID for a form
 *
 * Creates a consistent ID based on the page URL and form characteristics.
 *
 * @param {HTMLFormElement} form - The form element
 * @returns {string} Unique draft ID
 */
function generateDraftId(form) {
	// Use form ID if available
	if (form.id) {
		return `${form.id}_${getPageIdentifier()}`;
	}

	// Use form action + index as fallback
	const action = form.getAttribute('action') || 'unknown';
	const formIndex = Array.from(document.forms).indexOf(form);

	return `form_${hashString(action)}_${formIndex}_${getPageIdentifier()}`;
}

/**
 * Get a page identifier based on URL
 *
 * @returns {string} Page identifier
 */
function getPageIdentifier() {
	const path = window.location.pathname;

	// Extract post ID from URL if present (e.g., /petition/123/)
	const idMatch = path.match(/\/(\d+)\/?$/);
	if (idMatch) {
		return idMatch[1];
	}

	// Use path hash
	return hashString(path).toString();
}

/**
 * Simple string hash function
 *
 * @param {string} str - String to hash
 * @returns {number} Hash value
 */
function hashString(str) {
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		const char = str.charCodeAt(i);
		hash = (hash << 5) - hash + char;
		hash = hash & hash; // Convert to 32bit integer
	}
	return Math.abs(hash);
}

// =============================================================================
// FORM DATA EXTRACTION
// =============================================================================

/**
 * Extract form data as an object
 *
 * Gathers all form field values, excluding sensitive and system fields.
 *
 * @param {HTMLFormElement} form - The form element
 * @returns {Object} Form data object
 */
function extractFormData(form) {
	const data = {};
	const formData = new FormData(form);

	for (const [key, value] of formData.entries()) {
		// Skip excluded fields
		if (DRAFT_CONFIG.excludedFields.includes(key)) {
			continue;
		}

		// Skip empty values
		if (value === '' || value === null || value === undefined) {
			continue;
		}

		// Handle multiple values (checkboxes, multi-selects)
		if (data[key] !== undefined) {
			if (Array.isArray(data[key])) {
				data[key].push(value);
			} else {
				data[key] = [data[key], value];
			}
		} else {
			data[key] = value;
		}
	}

	return data;
}

/**
 * Restore form data from a draft
 *
 * Populates form fields with saved draft values.
 *
 * @param {HTMLFormElement} form - The form element
 * @param {Object} data - Draft data to restore
 */
function restoreFormData(form, data) {
	if (!data || typeof data !== 'object') {
		return;
	}

	for (const [key, value] of Object.entries(data)) {
		// Skip excluded fields
		if (DRAFT_CONFIG.excludedFields.includes(key)) {
			continue;
		}

		const elements = form.elements[key];

		if (!elements) {
			continue;
		}

		// Handle NodeList (radio buttons, checkboxes with same name)
		if (elements instanceof NodeList || elements instanceof RadioNodeList) {
			restoreElementGroup(elements, value);
		} else {
			restoreElement(elements, value);
		}
	}
}

/**
 * Restore a group of elements (radio buttons, checkboxes)
 *
 * @param {NodeList|RadioNodeList} elements - Group of elements
 * @param {*} value - Value to restore
 */
function restoreElementGroup(elements, value) {
	const values = Array.isArray(value) ? value : [value];

	for (const element of elements) {
		if (element.type === 'radio' || element.type === 'checkbox') {
			element.checked = values.includes(element.value);
		} else {
			restoreElement(element, value);
		}
	}
}

/**
 * Restore a single element's value
 *
 * @param {HTMLElement} element - The element to restore
 * @param {*} value - Value to restore
 */
function restoreElement(element, value) {
	switch (element.type) {
		case 'checkbox':
			element.checked = Boolean(value);
			break;

		case 'radio':
			element.checked = element.value === value;
			break;

		case 'select-multiple': {
			const values = Array.isArray(value) ? value : [value];
			for (const option of element.options) {
				option.selected = values.includes(option.value);
			}
			break;
		}

		case 'file':
			// Cannot restore file inputs for security reasons
			break;

		default:
			element.value = value;

			// Trigger change event for any listeners
			element.dispatchEvent(new Event('change', { bubbles: true }));
	}
}

// =============================================================================
// DRAFT TYPE DETECTION
// =============================================================================

/**
 * Detect the type of form for draft categorization
 *
 * @param {HTMLFormElement} form - The form element
 * @returns {string} Draft type from DRAFT_TYPES
 */
function detectDraftType(form) {
	const formId = form.id || '';
	const formClass = form.className || '';
	const action = form.getAttribute('action') || '';

	// Check for petition forms
	if (
		formId.includes('petition') ||
    formClass.includes('petition') ||
    action.includes('petition')
	) {
		return DRAFT_TYPES.PETITION;
	}

	// Check for consultation forms
	if (
		formId.includes('consultation') ||
    formClass.includes('consultation') ||
    action.includes('consultation')
	) {
		return DRAFT_TYPES.CONSULTATION;
	}

	return DRAFT_TYPES.GENERIC;
}

/**
 * Generate a display title for the draft
 *
 * @param {HTMLFormElement} form - The form element
 * @param {Object} data - Form data
 * @returns {string} Human-readable title
 */
function generateDraftTitle(form, data) {
	// Try to find a title field
	const titleFields = ['title', 'petition_title', 'name', 'subject'];

	for (const field of titleFields) {
		if (data[field]) {
			return data[field].substring(0, 100);
		}
	}

	// Fallback to form type + date
	const type = detectDraftType(form);
	const date = new Date().toLocaleDateString();

	return `${type.charAt(0).toUpperCase() + type.slice(1)} Draft - ${date}`;
}

// =============================================================================
// SAVING OPERATIONS
// =============================================================================

/**
 * Save form as draft
 *
 * Stores the current form state in IndexedDB.
 *
 * @param {HTMLFormElement} form - The form element
 * @param {boolean} force - Whether to save even if data hasn't changed
 * @returns {Promise<void>}
 */
export async function saveFormDraft(form, force = false) {
	const draftId = generateDraftId(form);
	const data = extractFormData(form);

	// Check if data has changed
	const dataString = JSON.stringify(data);
	if (!force && lastSavedData.get(form) === dataString) {
		return;
	}

	// Update last saved data
	lastSavedData.set(form, dataString);

	// Skip if no data
	if (Object.keys(data).length === 0) {
		return;
	}

	try {
		await saveDraft({
			id: draftId,
			type: detectDraftType(form),
			data: data,
			title: generateDraftTitle(form, data),
		});

		// Also save to localStorage as backup
		saveToLocalStorage(draftId, data);

		dispatchDraftEvent('draftSaved', { form, draftId, data });
	} catch (error) {
		// Failed to save draft (logged silently)

		// Fallback to localStorage only
		saveToLocalStorage(draftId, data);
	}
}

/**
 * Save to localStorage as backup
 *
 * @param {string} draftId - Draft ID
 * @param {Object} data - Draft data
 */
function saveToLocalStorage(draftId, data) {
	try {
		const key = DRAFT_CONFIG.localStoragePrefix + draftId;
		localStorage.setItem(
			key,
			JSON.stringify({
				data,
				timestamp: Date.now(),
			}),
		);
	} catch (error) {
		// localStorage might be full or disabled (silent)
	}
}

/**
 * Schedule a debounced draft save
 *
 * @param {HTMLFormElement} form - The form element
 */
function scheduleDraftSave(form) {
	// Clear existing timeout
	const existingTimeout = saveTimeouts.get(form);
	if (existingTimeout) {
		clearTimeout(existingTimeout);
	}

	// Schedule new save
	const timeout = setTimeout(() => {
		saveFormDraft(form);
		saveTimeouts.delete(form);
	}, DRAFT_CONFIG.saveDebounceMs);

	saveTimeouts.set(form, timeout);
}

// =============================================================================
// RESTORATION OPERATIONS
// =============================================================================

/**
 * Restore a form from its draft
 *
 * Loads and populates form with saved draft data.
 *
 * @param {HTMLFormElement} form - The form element
 * @param {boolean} showNotification - Whether to show a notification
 * @returns {Promise<boolean>} True if draft was restored
 */
export async function restoreFormDraft(form, showNotification = true) {
	const draftId = generateDraftId(form);

	try {
		// Try IndexedDB first
		let draft = await getDraft(draftId);

		// Fallback to localStorage
		if (!draft) {
			draft = getFromLocalStorage(draftId);
		}

		if (!draft || !draft.data || Object.keys(draft.data).length === 0) {
			return false;
		}

		// Restore the data
		restoreFormData(form, draft.data);

		// Update last saved data to prevent immediate re-save
		lastSavedData.set(form, JSON.stringify(draft.data));

		dispatchDraftEvent('draftRestored', { form, draftId, draft });

		// Show notification
		if (showNotification) {
			showDraftRestoredNotification(form, draft);
		}

		return true;
	} catch (error) {
		// Failed to restore draft (logged silently)
		return false;
	}
}

/**
 * Get draft from localStorage backup
 *
 * @param {string} draftId - Draft ID
 * @returns {Object|null} Draft object or null
 */
function getFromLocalStorage(draftId) {
	try {
		const key = DRAFT_CONFIG.localStoragePrefix + draftId;
		const stored = localStorage.getItem(key);

		if (!stored) {
			return null;
		}

		const parsed = JSON.parse(stored);

		// Check if too old (7 days)
		const maxAge = 7 * 24 * 60 * 60 * 1000;
		if (Date.now() - parsed.timestamp > maxAge) {
			localStorage.removeItem(key);
			return null;
		}

		return {
			id: draftId,
			data: parsed.data,
			updatedAt: parsed.timestamp,
		};
	} catch {
		return null;
	}
}

/**
 * Show notification that draft was restored
 *
 * @param {HTMLFormElement} form - The form element
 * @param {Object} draft - The draft object
 */
function showDraftRestoredNotification(form, draft) {
	// Create notification element
	const notification = document.createElement('div');
	notification.className = 'draft-restored-notification';
	notification.setAttribute('role', 'alert');
	notification.setAttribute('aria-live', 'polite');
	notification.innerHTML = `
    <div class="draft-notification-content">
      <span class="draft-notification-icon">💾</span>
      <span class="draft-notification-text">
        Your previous draft has been restored.
      </span>
      <button type="button" class="draft-notification-dismiss" aria-label="Dismiss notification">
        ✕
      </button>
    </div>
  `;

	// Add styles
	notification.style.cssText = `
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
    color: white;
    padding: 16px 24px;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    z-index: 10000;
    animation: slideUp 0.3s ease-out;
    font-family: system-ui, -apple-system, sans-serif;
  `;

	// Add animation keyframes
	if (!document.getElementById('draft-notification-styles')) {
		const style = document.createElement('style');
		style.id = 'draft-notification-styles';
		style.textContent = `
      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateX(-50%) translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateX(-50%) translateY(0);
        }
      }
      .draft-notification-content {
        display: flex;
        align-items: center;
        gap: 12px;
      }
      .draft-notification-dismiss {
        background: rgba(255,255,255,0.2);
        border: none;
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
      }
      .draft-notification-dismiss:hover {
        background: rgba(255,255,255,0.3);
      }
    `;
		document.head.appendChild(style);
	}

	document.body.appendChild(notification);

	// Dismiss button handler
	const dismissBtn = notification.querySelector('.draft-notification-dismiss');
	dismissBtn?.addEventListener('click', () => {
		notification.remove();
	});

	// Auto-dismiss after 5 seconds
	setTimeout(() => {
		if (notification.parentElement) {
			notification.style.animation = 'slideUp 0.3s ease-out reverse';
			setTimeout(() => notification.remove(), 300);
		}
	}, 5000);
}

// =============================================================================
// CLEANUP OPERATIONS
// =============================================================================

/**
 * Clear a form's draft
 *
 * Called after successful form submission.
 *
 * @param {HTMLFormElement} form - The form element
 * @returns {Promise<void>}
 */
export async function clearFormDraft(form) {
	const draftId = generateDraftId(form);

	try {
		// Remove from IndexedDB
		await removeDraft(draftId);

		// Remove from localStorage
		const key = DRAFT_CONFIG.localStoragePrefix + draftId;
		localStorage.removeItem(key);

		// Clear tracked data
		lastSavedData.delete(form);

		dispatchDraftEvent('draftCleared', { form, draftId });
	} catch (error) {
		// Failed to clear draft (logged silently)
	}
}

/**
 * Clear all drafts of a specific type
 *
 * @param {string} type - Draft type from DRAFT_TYPES
 * @returns {Promise<number>} Number of drafts cleared
 */
export async function clearAllDraftsOfType(type) {
	const drafts = await getAllDrafts(type);

	let cleared = 0;
	for (const draft of drafts) {
		try {
			await removeDraft(draft.id);

			// Also remove from localStorage
			const key = DRAFT_CONFIG.localStoragePrefix + draft.id;
			localStorage.removeItem(key);

			cleared++;
		} catch (error) {
			// Failed to clear draft (silent)
		}
	}

	return cleared;
}

// =============================================================================
// FORM REGISTRATION
// =============================================================================

/**
 * Register a form for draft saving
 *
 * Sets up event listeners for auto-save and restoration.
 *
 * @param {HTMLFormElement} form - The form element
 * @param {Object} options - Configuration options
 * @param {boolean} [options.autoRestore=true] - Restore draft on registration
 * @param {boolean} [options.clearOnSubmit=true] - Clear draft on successful submit
 */
export function registerForm(form, options = {}) {
	const { autoRestore = true, clearOnSubmit = true } = options;

	if (registeredForms.has(form)) {
		return;
	}

	registeredForms.add(form);

	// Add input event listener for auto-save
	form.addEventListener('input', () => {
		scheduleDraftSave(form);
	});

	// Add change event listener (for selects, checkboxes, etc.)
	form.addEventListener('change', () => {
		scheduleDraftSave(form);
	});

	// Clear draft on successful submission
	if (clearOnSubmit) {
		form.addEventListener('submit', () => {
			// Clear after a short delay to allow the submit to complete
			setTimeout(() => {
				clearFormDraft(form);
			}, 1000);
		});
	}

	// Auto-restore draft
	if (autoRestore) {
		restoreFormDraft(form);
	}

	// Mark form as draft-enabled
	form.setAttribute('data-draft-enabled', 'true');

	dispatchDraftEvent('formRegistered', { form });
}

/**
 * Unregister a form from draft saving
 *
 * @param {HTMLFormElement} form - The form element
 */
export function unregisterForm(form) {
	registeredForms.delete(form);

	// Clear any pending save
	const timeout = saveTimeouts.get(form);
	if (timeout) {
		clearTimeout(timeout);
		saveTimeouts.delete(form);
	}

	lastSavedData.delete(form);
	form.removeAttribute('data-draft-enabled');

	// Form unregistered (silent)
}

/**
 * Auto-register all matching forms on the page
 */
export function autoRegisterForms() {
	const selector = DRAFT_CONFIG.formSelectors.join(', ');
	const forms = document.querySelectorAll(selector);

	forms.forEach((form) => {
		if (form instanceof HTMLFormElement) {
			// Skip forms that have inline handlers (they handle their own drafts)
			if (form.getAttribute('data-no-pwa-intercept') === 'true') {
				return;
			}
			registerForm(form);
		}
	});

	// Auto-registered forms (silent)
}

// =============================================================================
// EVENT DISPATCHING
// =============================================================================

/**
 * Dispatch a custom draft event
 *
 * @param {string} eventName - Name of the event
 * @param {Object} detail - Event detail data
 */
function dispatchDraftEvent(eventName, detail = {}) {
	if (typeof window !== 'undefined') {
		const event = new CustomEvent(`draftManager:${eventName}`, { detail });
		window.dispatchEvent(event);
	}
}

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Initialize the draft manager
 */
export function initDraftManager() {
	// Auto-register forms on page load
	autoRegisterForms();

	// Watch for dynamically added forms
	const observer = new MutationObserver((mutations) => {
		for (const mutation of mutations) {
			for (const node of mutation.addedNodes) {
				if (node instanceof HTMLFormElement) {
					const selector = DRAFT_CONFIG.formSelectors.join(', ');
					if (node.matches(selector)) {
						registerForm(node);
					}
				} else if (node instanceof Element) {
					const forms = node.querySelectorAll(
						DRAFT_CONFIG.formSelectors.join(', '),
					);
					forms.forEach((form) => {
						if (form instanceof HTMLFormElement) {
							registerForm(form);
						}
					});
				}
			}
		}
	});

	observer.observe(document.body, {
		childList: true,
		subtree: true,
	});

	// Draft manager initialized (silent)
}

// Auto-initialize if in browser environment
if (typeof window !== 'undefined') {
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initDraftManager);
	} else {
		initDraftManager();
	}
}
